import { computed, ref } from 'vue';
import { getInsightSavedHintDetails } from '@/api/insights';
import type { InsightSavedHintDetail } from '@/types/insights';
import { STATES, type SwrState } from '@/utils/swr';

export const SAVED_INSIGHTS_PAGE_LIMIT = 20;

const keyOf = (detail: InsightSavedHintDetail): string => detail.hint.uuid;

const hints = ref<InsightSavedHintDetail[]>([]);
const state = ref<SwrState>('idle');
const isLoadingNextPage = ref(false);
const hasNextPage = ref(false);
const nextPageError = ref(false);
const nextPage = ref(1);
const removedHints = ref(new Map<string, InsightSavedHintDetail | undefined>());
const generation = ref(0);

export const clearSavedInsightDetailsPagination = (): void => {
  generation.value += 1;
  hints.value = [];
  state.value = 'idle';
  isLoadingNextPage.value = false;
  hasNextPage.value = false;
  nextPageError.value = false;
  nextPage.value = 1;
  removedHints.value = new Map();
};

const fetchPage = async (page: number): Promise<void> => {
  const requested = generation.value;
  try {
    const fetched = await getInsightSavedHintDetails(
      page,
      SAVED_INSIGHTS_PAGE_LIMIT,
    );
    if (requested !== generation.value) return;
    const accumulated = new Set(hints.value.map(keyOf));
    hints.value = [
      ...hints.value,
      ...fetched.savedHints.filter(
        hint =>
          !removedHints.value.has(keyOf(hint)) && !accumulated.has(keyOf(hint)),
      ),
    ];
    hasNextPage.value = fetched.hasNextPage;
    nextPageError.value = false;
    nextPage.value = page + 1;
    state.value = STATES.SUCCESS;
  } catch {
    if (requested !== generation.value) return;
    if (page === 1) {
      hasNextPage.value = false;
      state.value = STATES.ERROR;
      return;
    }
    nextPageError.value = true;
  }
};

const reset = (): void => {
  clearSavedInsightDetailsPagination();
  state.value = STATES.PENDING;
  void fetchPage(1);
};

const loadNextPage = async (): Promise<void> => {
  if (
    isLoadingNextPage.value ||
    !hasNextPage.value ||
    nextPageError.value ||
    state.value !== STATES.SUCCESS
  ) {
    return;
  }
  const requested = generation.value;
  isLoadingNextPage.value = true;
  try {
    await fetchPage(nextPage.value);
  } finally {
    if (requested === generation.value) isLoadingNextPage.value = false;
  }
};

const retryNextPage = async (): Promise<void> => {
  nextPageError.value = false;
  await loadNextPage();
};

const removeHint = (hintUuid: string): void => {
  removedHints.value.set(
    hintUuid,
    hints.value.find(hint => keyOf(hint) === hintUuid),
  );
  hints.value = hints.value.filter(hint => keyOf(hint) !== hintUuid);
};

export const restoreSavedInsightHint = (hintUuid: string): void => {
  if (!removedHints.value.has(hintUuid)) return;
  const restored = removedHints.value.get(hintUuid);
  removedHints.value.delete(hintUuid);
  if (restored) hints.value = [restored, ...hints.value];
};

export const useSavedInsightDetailsPagination = () => ({
  hints: computed(() => hints.value),
  state: computed(() => state.value),
  isLoadingNextPage: computed(() => isLoadingNextPage.value),
  hasNextPage: computed(() => hasNextPage.value),
  nextPageError: computed(() => nextPageError.value),
  loadNextPage,
  retryNextPage,
  reset,
  removeHint,
});
